import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CPLedger, { LadderEditor } from './CPLedger';
import RevealContact from './RevealContact';
import { Link } from 'react-router-dom';

const ChannelPartners = ({ apiBase }) => {
  const [partners, setPartners] = useState([]);
  const [ledgerId, setLedgerId] = useState(null);
  const [ladderOpen, setLadderOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    alternate_phone: '',
    whatsapp_number: '',
    address: '',
    location: '',
    sub_location: '',
    pan_number: '',
    gst_number: '',
    rera_number: '',
    status: 'active',
    status_classification: 'active',
    priority_classification: 'P2',
    commission_rate: 2.0,
    notes: ''
  });
  const [reraSearch, setReraSearch] = useState('');
  const [reraResults, setReraResults] = useState([]);
  const [reraVerified, setReraVerified] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${apiBase}/channel-partners`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error fetching channel partners:', error);
      alert('Error loading channel partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`${apiBase}/channel-partners/${editingPartner.id}`, formData);
        alert('Channel partner updated successfully!');
      } else {
        await axios.post(`${apiBase}/channel-partners`, formData);
        alert('Channel partner created successfully!');
      }
      setShowModal(false);
      setEditingPartner(null);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving channel partner:', error);
      alert('Error saving channel partner: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      company_name: partner.company_name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      alternate_phone: partner.alternate_phone || '',
      whatsapp_number: partner.whatsapp_number || '',
      address: partner.address || '',
      location: partner.location || '',
      sub_location: partner.sub_location || '',
      pan_number: partner.pan_number || '',
      gst_number: partner.gst_number || '',
      rera_number: partner.rera_number || '',
      status: partner.status || 'active',
      status_classification: partner.status_classification || 'active',
      priority_classification: partner.priority_classification || 'P2',
      commission_rate: partner.commission_rate || 2.0,
      notes: partner.notes || ''
    });
    setReraVerified(!!partner.rera_number);
    setShowModal(true);
  };

  const handleDelete = (partner) => {
    setEditingPartner(partner);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!editingPartner) return;

    try {
      await axios.delete(`${apiBase}/channel-partners/${editingPartner.id}`);
      alert('Channel partner deleted successfully!');
      setShowDeleteConfirm(false);
      setEditingPartner(null);
      fetchPartners();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      const errorDetails = error.response?.data?.message || '';
      alert(`Error deleting channel partner: ${errorMsg}\n\n${errorDetails}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company_name: '',
      email: '',
      phone: '',
      alternate_phone: '',
      whatsapp_number: '',
      address: '',
      location: '',
      sub_location: '',
      pan_number: '',
      gst_number: '',
      rera_number: '',
      status: 'active',
      status_classification: 'active',
      priority_classification: 'P2',
      commission_rate: 2.0,
      notes: ''
    });
    setReraSearch('');
    setReraResults([]);
    setReraVerified(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'inactive': 'secondary',
      'hyper active': 'info',
      'super active': 'primary',
      'to ignite': 'warning',
      'dormant': 'danger'
    };
    return colors[status?.toLowerCase()] || 'secondary';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'P1': '#dc2626',
      'P2': '#f59e0b',
      'P3': '#10b981'
    };
    return colors[priority] || '#64748b';
  };

  const filteredPartners = partners.filter(partner => {
    const matchesStatus = filterStatus === 'all' || partner.status_classification === filterStatus;
    const matchesPriority = filterPriority === 'all' || partner.priority_classification === filterPriority;
    const matchesSearch = !searchTerm ||
      partner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.phone?.includes(searchTerm);
    return matchesStatus && matchesPriority && matchesSearch;
  });

  if (loading) {
    return <div className="loading">Loading channel partners...</div>;
  }

  if (selectedPartner) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <button
              className="btn btn-secondary"
              onClick={() => setSelectedPartner(null)}
              style={{ marginBottom: '12px' }}
            >
              ← Back to Channel Partners
            </button>
            <h1>{selectedPartner.name}</h1>
            <p style={{ color: '#64748b', marginTop: '4px' }}>
              {selectedPartner.company_name} • {selectedPartner.location} {selectedPartner.sub_location ? `- ${selectedPartner.sub_location}` : ''}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => handleEdit(selectedPartner)}>
            Edit Partner
          </button>
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 className="card-title">Partner Overview</h2>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div className="stat-card">
              <div className="stat-label">Status Classification</div>
              <div className="stat-value">
                <span className={`badge badge-${getStatusColor(selectedPartner.status_classification)}`}>
                  {selectedPartner.status_classification || 'active'}
                </span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Priority Classification</div>
              <div className="stat-value">
                <span className="badge" style={{
                  backgroundColor: getPriorityColor(selectedPartner.priority_classification) + '20',
                  color: getPriorityColor(selectedPartner.priority_classification)
                }}>
                  {selectedPartner.priority_classification || 'P2'}
                </span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Clients</div>
              <div className="stat-value">{selectedPartner.total_clients || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Sales</div>
              <div className="stat-value">{selectedPartner.total_sales || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Bookings</div>
              <div className="stat-value">{selectedPartner.total_bookings || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Revenue</div>
              <div className="stat-value">₹{formatCurrency(selectedPartner.total_revenue || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Commission Earned</div>
              <div className="stat-value">₹{formatCurrency(selectedPartner.total_commission_earned || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Commission Rate</div>
              <div className="stat-value">{selectedPartner.commission_rate || 2.0}%</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card-title">Contact Information</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Phone</div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                <RevealContact row={selectedPartner} field="phone" entityType="broker" apiBase={apiBase} />
              </div>
            </div>
            {selectedPartner.alternate_phone && (
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Alternate Phone</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedPartner.alternate_phone}</div>
              </div>
            )}
            {selectedPartner.whatsapp_number && (
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>WhatsApp</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedPartner.whatsapp_number}</div>
              </div>
            )}
            {selectedPartner.email && (
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedPartner.email}</div>
              </div>
            )}
            {selectedPartner.location && (
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Location</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>
                  {selectedPartner.location} {selectedPartner.sub_location ? `- ${selectedPartner.sub_location}` : ''}
                </div>
              </div>
            )}
            {selectedPartner.address && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Address</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedPartner.address}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Channel Partners / Brokers</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-secondary" onClick={() => setLadderOpen(true)}>📶 Brokerage ladder</button>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setEditingPartner(null);
            setShowModal(true);
          }}
        >
          + New Channel Partner
        </button>
        </div>
      </div>
      {ledgerId && <CPLedger apiBase={apiBase} partnerId={ledgerId} onClose={() => setLedgerId(null)} />}
      {ladderOpen && <LadderEditor apiBase={apiBase} onClose={() => setLadderOpen(false)} />}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, company, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Status Classification</label>
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="hyper active">Hyper Active</option>
              <option value="super active">Super Active</option>
              <option value="to ignite">To Ignite</option>
              <option value="dormant">Dormant</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Priority Classification</label>
            <select
              className="form-select"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="all">All Priorities</option>
              <option value="P1">P1 (Highest)</option>
              <option value="P2">P2 (Medium)</option>
              <option value="P3">P3 (Low)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title">Summary Statistics</h2>
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <div className="stat-card">
            <div className="stat-label">Total Partners</div>
            <div className="stat-value">{partners.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Partners</div>
            <div className="stat-value">
              {partners.filter(p => p.status_classification === 'active' || p.status_classification === 'hyper active' || p.status_classification === 'super active').length}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">P1 Partners</div>
            <div className="stat-value">{partners.filter(p => p.priority_classification === 'P1').length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Clients</div>
            <div className="stat-value">
              {partners.reduce((sum, p) => sum + (parseInt(p.total_clients) || 0), 0)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Bookings</div>
            <div className="stat-value">
              {partners.reduce((sum, p) => sum + (parseInt(p.total_bookings) || 0), 0)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">
              ₹{formatCurrency(partners.reduce((sum, p) => sum + (parseFloat(p.total_revenue) || 0), 0))}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Channel Partners ({filteredPartners.length})</h2>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Location</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Clients</th>
                <th>Bookings</th>
                <th>Revenue</th>
                <th>Commission</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>
                    No channel partners found
                  </td>
                </tr>
              ) : (
                filteredPartners.map((partner) => (
                  <tr key={partner.id}>
                    <td>
                      <strong>{partner.name}</strong>
                      {partner.email && (
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{partner.email}</div>
                      )}
                    </td>
                    <td>{partner.company_name || '-'}</td>
                    <td>
                      {partner.location || '-'}
                      {partner.sub_location && (
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{partner.sub_location}</div>
                      )}
                    </td>
                    <td>
                      <RevealContact row={partner} field="phone" entityType="broker" apiBase={apiBase} />
                    </td>
                    <td>
                      <span className={`badge badge-${getStatusColor(partner.status_classification)}`}>
                        {partner.status_classification || 'active'}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: getPriorityColor(partner.priority_classification) + '20',
                        color: getPriorityColor(partner.priority_classification)
                      }}>
                        {partner.priority_classification || 'P2'}
                      </span>
                    </td>
                    <td>{partner.total_clients || 0}</td>
                    <td>{partner.total_bookings || 0}</td>
                    <td>₹{formatCurrency(partner.total_revenue || 0)}</td>
                    <td>₹{formatCurrency(partner.total_commission_earned || 0)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => setSelectedPartner(partner)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleEdit(partner)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: '11px', padding: '4px 8px', background: '#059669' }}
                          onClick={() => setLedgerId(partner.id)}
                        >
                          📒 Clients & KYC
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleDelete(partner)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingPartner ? 'Edit Channel Partner' : 'Add New Channel Partner'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* RERA Quick Import Section */}
              {!editingPartner && (
                <div style={{
                  marginBottom: '20px', padding: '16px', background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
                  borderRadius: '12px', border: '2px solid #86efac'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>🔍</span>
                    <span style={{ fontWeight: '700', color: '#166534' }}>Quick Import from RERA Database</span>
                    <span style={{ fontSize: '12px', color: '#15803d', background: '#bbf7d0', padding: '2px 8px', borderRadius: '10px' }}>23,593 verified agents</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search by phone, RERA number, or name..."
                      value={reraSearch}
                      onChange={async (e) => {
                        const query = e.target.value;
                        setReraSearch(query);
                        if (query.length >= 3) {
                          try {
                            const res = await axios.get(`${apiBase}/rera-agents/lookup?q=${encodeURIComponent(query)}`);
                            setReraResults(res.data || []);
                          } catch (err) { console.error(err); setReraResults([]); }
                        } else {
                          setReraResults([]);
                        }
                      }}
                      style={{ paddingRight: '100px' }}
                    />
                    {reraVerified && (
                      <span style={{
                        position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                        background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600'
                      }}>✅ RERA Verified</span>
                    )}
                  </div>
                  {reraResults.length > 0 && (
                    <div style={{
                      marginTop: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
                      maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                    }}>
                      {reraResults.map(agent => (
                        <div
                          key={agent.ID}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              name: agent.Name || '',
                              company_name: agent.Company || '',
                              phone: agent.Mobile || '',
                              email: agent.Mail || '',
                              address: agent.Address || '',
                              location: agent.Location || '',
                              rera_number: agent.RERA_CertNo || ''
                            });
                            setReraSearch(`${agent.Name} (${agent.RERA_CertNo})`);
                            setReraResults([]);
                            setReraVerified(true);
                          }}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f0fdf4'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{agent.Name}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '12px', marginTop: '3px' }}>
                            <span>📱 {agent.Mobile}</span>
                            <span style={{ color: '#10b981', fontWeight: '600' }}>🆔 {agent.RERA_CertNo}</span>
                            <span>📍 {agent.Location}</span>
                          </div>
                          {agent.Company && <div style={{ fontSize: '11px', color: '#8b5cf6', marginTop: '2px' }}>🏢 {agent.Company}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Alternate Phone</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.alternate_phone}
                    onChange={(e) => setFormData({ ...formData, alternate_phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Mumbai"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Sub Location</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.sub_location}
                    onChange={(e) => setFormData({ ...formData, sub_location: e.target.value })}
                    placeholder="e.g., Andheri East"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status Classification *</label>
                  <select
                    className="form-select"
                    value={formData.status_classification}
                    onChange={(e) => setFormData({ ...formData, status_classification: e.target.value })}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="hyper active">Hyper Active</option>
                    <option value="super active">Super Active</option>
                    <option value="to ignite">To Ignite</option>
                    <option value="dormant">Dormant</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority Classification *</label>
                  <select
                    className="form-select"
                    value={formData.priority_classification}
                    onChange={(e) => setFormData({ ...formData, priority_classification: e.target.value })}
                    required
                  >
                    <option value="P1">P1 (Highest Priority)</option>
                    <option value="P2">P2 (Medium Priority)</option>
                    <option value="P3">P3 (Low Priority)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Commission Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-input"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || 2.0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">PAN Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">GST Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-input"
                  rows="2"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingPartner ? 'Update' : 'Create'} Partner</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && editingPartner && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Confirm Delete</h3>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                Are you sure you want to delete <strong>{editingPartner.name}</strong>?
              </p>
              <div style={{
                padding: '12px',
                background: '#fef3c7',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#92400e'
              }}>
                <strong>⚠️ Warning:</strong> This action cannot be undone.
                {(editingPartner.total_clients > 0 || editingPartner.total_sales > 0) && (
                  <div style={{ marginTop: '8px' }}>
                    This partner has {editingPartner.total_clients || 0} clients and {editingPartner.total_sales || 0} sales.
                    You must reassign or remove these before deleting.
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setEditingPartner(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                >
                  Delete Partner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

export default ChannelPartners;
